import { useMemo } from 'react';
import { supabaseCoreClient } from '../supabase.ts';
import { callRpc } from '../supabase-operations.js';
import { useQuery } from "@supabase-cache-helpers/postgrest-react-query";
import { encode } from "@supabase-cache-helpers/postgrest-react-query";
import { queryClient } from '../constants.js';
import { commonQueryProps } from './cache.js';
import type { RelationshipLookup, ProcessedRelationshipMetadata } from '../../types/metadata.js';
import type { CachedQueryResult } from '../../types/supabase.js';
import { persistedRelations, useMetadataBootReady } from '../persistentMetadataCache.js';

/**
 * Builds the Supabase query for fetching relationship metadata
 * @returns PostgrestBuilder query for relationship_lookup with all necessary columns
 */
export const buildQueryForRelationships = () => {
  return supabaseCoreClient
    .from('relationship_lookup')
    .select('*');
};

/**
 * Post-processes relationship data by grouping by source table
 * @param data - Raw relationship data from database
 * @returns Processed relationship metadata grouped by table
 */
export const postProcessRelationships = (data: RelationshipLookup[]): ProcessedRelationshipMetadata => {
  const groupedByTable: ProcessedRelationshipMetadata = {
    flattened: {},
    byId: {},
    asList: []
  };

  data.forEach(rel => {
    const source = rel.source_table_name;
    if (!source || !rel.col_name) return;
    if (!groupedByTable[source]) {
      groupedByTable[source] = { asList: [], byFieldName: {} };
    }
    groupedByTable[source].asList.push(rel);
    groupedByTable[source].byFieldName[rel.col_name] = rel;
    groupedByTable.flattened[`${rel.source_table_name}_${rel.id}`] = rel;
    groupedByTable.byId[rel.id] = rel;
    groupedByTable.asList.push(rel);
  });

  return groupedByTable;
};

// Result type for relationship operations
interface RelationshipResult {
  data: ProcessedRelationshipMetadata | null;
  error: any;
}

/**
 * Fetches relationships data outside of React context
 * @returns Promise with processed relationship metadata or error
 */
export const getRelationships = async (): Promise<RelationshipResult> => {
  const query = buildQueryForRelationships();
  let relationshipData: RelationshipLookup[] | null = null;
  let relationshipError: any = null;
  
  try{
    // TypeScript cannot infer the Supabase cache structure - see SupabaseCacheData type definition
    const cachedResult = queryClient.getQueryData(encode(query, false)) as CachedQueryResult<RelationshipLookup[]>;
    relationshipData = cachedResult?.data ?? null;
  } catch {
    // Cache miss or error, will fetch from API
  }
  
  if (!relationshipData) {
    const { data, error } = await query;
    relationshipData = data;
    relationshipError = error;
  }

  if (relationshipError) {
    return { data: null, error: relationshipError };
  }

  const processedData = postProcessRelationships(relationshipData ?? []);
  return { data: processedData, error: null };
};

// Hook options interface
interface UseRelationshipsOptions {
  enabled?: boolean;
}

// Hook result interface
interface UseRelationshipsResult {
  data: ProcessedRelationshipMetadata | null;
  error: any;
  isLoading: boolean;
}

/**
 * React hook to fetch relationships data using React Query
 * @param options - Options including enabled flag
 * @returns Relationship data with loading and error states
 */
export const useRelationships = ({ enabled }: UseRelationshipsOptions): UseRelationshipsResult => {
  const bootReady = useMetadataBootReady();
  const query = buildQueryForRelationships();

  const { data, error, isPending } = useQuery(
    query,
    {
      ...commonQueryProps,
      enabled: enabled && bootReady,
      meta: persistedRelations('core_crm.relationship_lookup')
    }
  );

  // Memoized on the query result so the processed object keeps a stable
  // identity across renders — consumers memoize off it.
  const processedData = useMemo(() => (data ? postProcessRelationships(data) : null), [data]);

  if (isPending || error || !data) {
    return {
      data: null,
      error,
      isLoading: isPending
    };
  }

  return {
    data: processedData,
    error: null,
    isLoading: false
  };
};


// Parameters for creating a new relationship field
interface CreateRelationshipFieldParams {
  sourceTableInfoId: number;
  targetTableInfoId: number;
  targetLabelField: string;
  cascadeDelete: boolean;
  fieldName: string;
  fieldLabel: string;
  fieldHelp: string;
  isRequired: boolean;
  isUnique: boolean;
  isIndexed: boolean;
  defaultValue?: any;
}

/**
 * Creates a new relationship field in the relationship_lookup table
 * @param params - Parameters for creating the relationship field
 * @returns Promise that resolves when the relationship is created
 */
export const createNewRelationshipField = async ({
  sourceTableInfoId, 
  targetTableInfoId, 
  targetLabelField, 
  cascadeDelete, 
  fieldName, 
  fieldLabel, 
  fieldHelp, 
  isRequired, 
  isUnique, 
  isIndexed,
  defaultValue
}: CreateRelationshipFieldParams): Promise<void> => {
    await callRpc({
        schema: 'core_crm',
        functionName: 'create_relationship_field',
        params: {
            p_source_table_info_id: sourceTableInfoId,
            p_target_table_info_id: targetTableInfoId,
            p_target_label_field: targetLabelField,
            p_cascade_delete: cascadeDelete,
            p_field_name: fieldName,
            p_field_label: fieldLabel,
            p_field_help: fieldHelp,
            p_field_is_required: isRequired,
            p_field_is_unique: isUnique,
            p_field_is_indexed: isIndexed,
            p_default_value: defaultValue !== undefined ? { value: defaultValue } : null
        }
    });
};
